import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const DAY_MS = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + DAY_MS;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + DAY_MS;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DAY_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + DAY_MS;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(DAY_MS);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = DAY_MS - remaining;
  const progress = Math.min(90, 74 + elapsed / DAY_MS * 16);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-forest border border-gold/50 rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_rgba(18,53,36,0.2)]" data-id="element-55" data-mp="1l13d1n2d7cxe">
      <div className="text-center" data-id="element-56" data-mp="2ihsirrufqzc2">
        <div className="flex items-center justify-center gap-2 text-gold font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-57" data-mp="2iityi9meca04">
          <AlarmClock size={20} className="animate-pulse" data-id="element-58" data-mp="2nd4tjay6r2gn" />
          <span data-id="element-59" data-mp="2nbjub80xevcg">⏰ The launch price ends in:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" role="timer" aria-live="off" data-id="element-60" data-mp="2jaq94mevbun9">
          <TimeBox value={pad(totalHours)} label="hrs" data-id="element-61" data-mp="29dvbe5qf5ya1" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-62" data-mp="29en51b0ghlxo">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-63" data-mp="24mwqstyeee0z" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-64" data-mp="24lbxt8rqf358">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-65" data-mp="1zolm0xmymtpm" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-ivory/90 mb-8" data-id="element-66" data-mp="1vn9taj8shsd7">
          After this, the price goes up and it won't come back down.
        </p>

        <div className="space-y-2" data-id="element-67" data-mp="1lokfp2nxeu2r">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-68" data-mp="1lpltvg08nwop">
            <span className="text-ivory/80" data-id="element-69" data-mp="15qg5xsfinp5">
              Launch price spots remaining
            </span>
            <span className="text-gold" data-id="element-70" data-mp="63i98v4t8fux">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-forest-dark rounded-full overflow-hidden border border-gold/40" data-id="element-71" data-mp="b5r45x18trzo">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gold rounded-full" data-id="element-72" data-mp="f5hq9nftrjxv" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-ivory/70 font-medium" data-id="element-73" data-mp="3nk9vpxt548x7">
            {Math.round(progress)}% of spots filled
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-ivory border-2 border-gold rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-74" data-mp="2zv74by8pf1pi">
      <div className="flex items-baseline justify-center gap-1" data-id="element-75" data-mp="2gs591il1ulp5">
        <span className="text-4xl md:text-5xl font-bold text-forest tabular-nums" data-id="element-76" data-mp="2btbv5p1uosog">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-warmgray" data-id="element-77" data-mp="2qii1ry30pl6d">
          {label}
        </span>
      </div>
    </div>;
}